import { useState, useEffect } from 'react';
import { useNavigate, Outlet, Link, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import { logout, selectUser } from '../store/slices/authSlice';
import { fetchFactories } from '../store/slices/factorySlice';
import FactorySwitcher from '../components/FactorySwitcher';
import { getRoleLabel, isGeneralAdmin } from '../utils/access';

import {
  HomeIcon,
  TruckIcon as VehicleIcon,
  UserIcon as DriverIcon,
  DocumentTextIcon,
  FireIcon,
  WrenchIcon,
  ChartBarIcon, // Raporlar için ikon
  UsersIcon
} from '@heroicons/react/24/outline';

export default function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const generalAdmin = isGeneralAdmin(user);
  const userId = user?.id;

  useEffect(() => {
    if (userId) {
      dispatch(fetchFactories());
    }
  }, [userId, dispatch]);

  useEffect(() => {
    // Kullanıcı bilgisini PrivateRoute yükler (bu bileşen ondan sonra çizilir); burada sadece
    // giriş yapılmamışsa yönlendir. Burada tekrar yüklemek /auth/me/ isteğini iki kez gönderiyordu.
    if (!user && !localStorage.getItem('token')) {
      navigate('/login');
    }
  }, [user, navigate]);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const getLinkClass = (path: string) => {
    return `inline-flex items-center whitespace-nowrap border-b-2 px-1 pt-1 text-sm font-medium ${
      isActive(path)
        ? 'border-blue-500 text-blue-600'
        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
    }`;
  };

  if (!user) {
    return null;
  }

  // Mobil menü: rol bazlı sayfalar, 3'lü kare ızgarada
  const mobileNavItems = [
    { to: '/', label: 'Ana Sayfa', icon: HomeIcon, show: true },
    { to: '/vehicles', label: 'Araçlar', icon: VehicleIcon, show: user.is_staff },
    { to: '/drivers', label: 'Sürücüler', icon: DriverIcon, show: user.is_staff },
    { to: '/trips', label: 'Seferler', icon: DocumentTextIcon, show: true },
    { to: '/fuel', label: 'Yakıt', icon: FireIcon, show: true },
    { to: '/maintenance', label: 'Bakım', icon: WrenchIcon, show: true },
    { to: '/users', label: 'Kullanıcılar', icon: UsersIcon, show: generalAdmin },
    { to: '/reports', label: 'Raporlar', icon: ChartBarIcon, show: !user.is_staff },
  ].filter((item) => item.show);

  return (
    <div className="min-h-screen min-w-0 overflow-x-hidden bg-gray-100">
      <nav className="bg-white shadow">
        {/* Genel admin menüsü (7 link + fabrika seçici) 1024px'e sığmıyordu; masaüstü menü xl'den itibaren açılır */}
        <div className="mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 justify-between">
            <div className="flex">
              <div className="flex flex-shrink-0 items-center">
                <Link to="/" className="flex items-center">
                  <div className="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-lg">F</span>
                  </div>
                  <span className="ml-2 text-lg sm:text-xl font-bold text-gray-900 whitespace-nowrap">Filo Yönetim</span>
                </Link>
              </div>
              <div className="hidden xl:ml-6 xl:flex xl:space-x-6 2xl:space-x-8">
                <Link to="/" className={getLinkClass('/')}>
                  Ana Sayfa
                </Link>
                
                {user.is_staff && (
                  <>
                    <Link to="/vehicles" className={getLinkClass('/vehicles')}>
                      Araçlar
                    </Link>
                    <Link to="/drivers" className={getLinkClass('/drivers')}>
                      Sürücüler
                    </Link>
                  </>
                )}
                
                <Link to="/trips" className={getLinkClass('/trips')}>
                  Seferler
                </Link>
                
                <Link to="/fuel" className={getLinkClass('/fuel')}>
                  Yakıt
                </Link>
                
                <Link to="/maintenance" className={getLinkClass('/maintenance')}>
                  Bakım
                </Link>

                {generalAdmin && (
                  <Link to="/users" className={getLinkClass('/users')}>
                    Kullanıcılar
                  </Link>
                )}
                
                {/* Raporlar bağlantısı sadece admin olmayanlar için */}
                {!user.is_staff && (
                  <Link to="/reports" className={getLinkClass('/reports')}>
                    Raporlar
                  </Link>
                )}
              </div>
            </div>
            
            <div className="hidden xl:ml-4 xl:flex xl:items-center">
              <div className="relative ml-3">
                <div className="flex items-center">
                  {generalAdmin && <FactorySwitcher className="mr-4 w-48" />}
                  <div className="flex items-center space-x-2 mr-4">
                    <div
                      className="w-8 h-8 flex-shrink-0 bg-gray-300 rounded-full flex items-center justify-center"
                      title={`${user.first_name} ${user.last_name} · ${getRoleLabel(user)}`}
                    >
                      <span className="text-sm font-medium text-gray-700">
                        {user.first_name?.[0]}{user.last_name?.[0]}
                      </span>
                    </div>
                    <div className="text-sm leading-tight whitespace-nowrap hidden 2xl:block">
                      <div className="font-medium text-gray-900">
                        {user.first_name} {user.last_name}
                      </div>
                      <div className="text-gray-500">
                        {getRoleLabel(user)}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="btn btn-secondary whitespace-nowrap"
                  >
                    Çıkış
                  </button>
                </div>
              </div>
            </div>
            
            {/* Mobile menu button */}
            <div className="flex items-center xl:hidden">
              <button
                type="button"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              >
                <span className="sr-only">Ana menüyü aç</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu, show/hide based on menu state. */}
        {isMenuOpen && (
          <div className="xl:hidden" id="mobile-menu">
            {generalAdmin && (
              <div className="px-3 pt-3">
                <FactorySwitcher />
              </div>
            )}
            <div className="grid grid-cols-3 gap-2.5 px-3 pt-3 pb-4">
              {mobileNavItems.map(({ to, label, icon: Icon }) => {
                const active = isActive(to);
                return (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => setIsMenuOpen(false)}
                    className={`flex aspect-square flex-col items-center justify-center gap-2 rounded-2xl border p-2 text-center text-sm font-semibold shadow-sm transition ${
                      active
                        ? 'border-indigo-300 bg-indigo-50 text-indigo-700 ring-1 ring-indigo-300'
                        : 'border-gray-200 bg-white text-gray-800 hover:bg-gray-50'
                    }`}
                  >
                    <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${active ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                      <Icon className="h-6 w-6" />
                    </span>
                    <span className="leading-tight">{label}</span>
                  </Link>
                );
              })}
            </div>
            <div className="border-t border-gray-200 pt-4 pb-3 flex items-center justify-between px-4">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center shadow-md text-white font-bold text-lg">
                  <span>{user.first_name?.[0]}{user.last_name?.[0]}</span>
                </div>
                <div className="ml-3">
                  <div className="text-base font-medium text-gray-800">
                    {user.first_name} {user.last_name}
                  </div>
                  <div className="text-sm font-medium text-gray-500">
                    {getRoleLabel(user)}
                  </div>
                </div>
              </div>
              <button
                onClick={() => {
                  handleLogout();
                  setIsMenuOpen(false);
                }}
                className="btn btn-danger"
              >
                Çıkış
              </button>
            </div>
          </div>
        )}
      </nav>

      <div className="flex flex-col min-h-[calc(100vh-64px)]"> {/* Navbar yüksekliği (64px) düşüldü ve flex eklenerek içerik doldurulması sağlandı */}
        <main className="min-w-0 flex-grow"> {/* main içeriğin esnek büyümesini sağlar */}
          <div className="mx-auto min-w-0 w-full max-w-7xl sm:px-6 lg:px-8"> {/* w-full eklendi */}
            {/* Sayfalar fabrika seçimini useFactoryScope ile okur ve değişince verilerini kendileri yeniden çeker */}
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
